/**
 * Placing a portalled menu at a measured screen position, under UI scale.
 *
 * The sidebar menus all do the same three things: measure an anchor with
 * "getBoundingClientRect", clamp the result into the viewport, then write it
 * back as "left"/"top". That is right at 100% and wrong at every other UI
 * scale, because the halves live in different coordinate spaces:
 *
 *  - "getBoundingClientRect" and "window.innerWidth"/"innerHeight" report
 *    SCREEN pixels — where the anchor actually is once "zoom" has been applied.
 *  - a "left"/"top" written on the menu is an ordinary CSS length on an element
 *    inside the zoomed subtree, so the engine scales it by the zoom AGAIN.
 *
 * The clamped screen coordinate therefore came back out double-scaled.
 * Measured in Chromium at 150%: a menu intended for (60, 684) landed at
 * (90, 1026) — past the bottom of an 800px viewport. The account menu is the
 * only pointer route into settings, so at the largest UI scale it became
 * unreachable.
 *
 * Dividing by the element's own zoom factor converts a screen coordinate back
 * into the space the style property is read in. Note the clamp itself needs no
 * change: rect coordinates and "innerWidth" are already the same space as each
 * other, and that space is the screen.
 */

/**
 * The effective zoom on an element, as a multiplier.
 *
 * "currentCSSZoom" is the exact answer and is what the platform added for this,
 * but it is not implemented everywhere. The fallback compares the two
 * geometries every element already exposes — "offsetWidth" is unzoomed CSS
 * pixels, the rect's width is zoomed — which needs no support beyond what the
 * measurement above already assumed. It is preferred second only because
 * "offsetWidth" is rounded to an integer, making it a fraction of a percent off
 * on a fractional width.
 *
 * Deliberately NOT read back off the host's inline style: the terminal does not
 * own the zoom, and an embedder may scale us by means we cannot see.
 */
export function zoomFactorOf(el: HTMLElement): number {
  const native = (el as HTMLElement & { currentCSSZoom?: number })
    .currentCSSZoom;
  if (typeof native === 'number' && Number.isFinite(native) && native > 0) {
    return native;
  }
  const width = el.offsetWidth;
  if (width === 0) return 1;
  const derived = el.getBoundingClientRect().width / width;
  // A detached or display:none element measures zero on both sides; treat an
  // unusable ratio as "not scaled" rather than propagating NaN into a style.
  return Number.isFinite(derived) && derived > 0 ? derived : 1;
}

/**
 * Convert a screen-space point into the coordinate space of "el"'s own
 * "left"/"top", so that writing the result puts the element where the point
 * actually is.
 */
export function toLocalPosition(
  el: HTMLElement,
  x: number,
  y: number,
): { readonly x: number; readonly y: number } {
  const factor = zoomFactorOf(el);
  return factor === 1 ? { x, y } : { x: x / factor, y: y / factor };
}

/**
 * The shared layer that portalled menus mount into.
 *
 * Radix positions a menu by measuring the trigger with `getBoundingClientRect`
 * — screen pixels, already multiplied by any `zoom` — and writing that back as
 * a `transform: translate(...)`. When the menu is portalled into the zoomed
 * subtree (`document.body`, which is where the UI scale is applied), the
 * engine scales that translate a second time, so the panel drifts from its
 * trigger by exactly `offset x (factor - 1)`. Measured in Chromium against the
 * settings dialog's Language dropdown:
 *
 *   100%: right edge  +0px      125%: +281px
 *   110%: right edge  +107px    150%: +605px
 *
 * At 150% the panel is most of a screen away from the control that opened it.
 * This is the same double-count `toLocalPosition` fixes for the menus we place
 * ourselves; Radix computes its own transform, so it cannot be corrected at
 * the write and is corrected at the coordinate space instead.
 *
 * The layer cancels the ambient zoom (`1 / factor`), so a translate written in
 * screen pixels lands where it says. The panel is then scaled back up by the
 * CSS variable below, which is set on the layer and consumed by each menu's
 * own surface — so the menu is the right size AND in the right place. Verified
 * to hold at 100/110/125/150.
 */
const MENU_LAYER_ID = 'dime-menu-layer';

/** CSS variable the layer publishes for menu surfaces to scale themselves by. */
export const MENU_ZOOM_VAR = '--dime-menu-zoom';

export function menuLayer(): HTMLElement | null {
  if (typeof document === 'undefined') return null;
  let layer = document.getElementById(MENU_LAYER_ID);
  if (layer == null) {
    layer = document.createElement('div');
    layer.id = MENU_LAYER_ID;
    document.body.appendChild(layer);
    // Seed the factor once at creation. Every open re-reads it via
    // `syncMenuLayer` (see the call sites' `onOpenChange`), which is the
    // moment it actually has to be right — syncing here on every call would
    // force a layout flush (`getBoundingClientRect`) on each render that
    // evaluates a Portal's `container` prop, and the composer's picker
    // renders on every keystroke.
    syncMenuLayerZoom(layer);
  }
  return layer;
}

/**
 * Re-read the ambient zoom, to be called as a menu OPENS.
 *
 * Not on render, which is where this started and why it did nothing: the scale
 * is applied by the host writing `zoom` onto an element, and that is invisible
 * to React — no state changed, so nothing re-rendered, and the layer kept
 * whatever factor happened to be current when it was created. An open is the
 * moment the factor actually has to be right, and it is also the moment Radix
 * measures.
 */
export function syncMenuLayer(): void {
  const layer =
    typeof document === 'undefined'
      ? null
      : document.getElementById(MENU_LAYER_ID);
  if (layer != null) syncMenuLayerZoom(layer);
}

/**
 * Re-read the ambient zoom and mirror it onto the layer.
 *
 * Measured rather than passed in: the terminal does not own the UI scale — the
 * host applies it, and an embedder may scale us by means we cannot see — so
 * the same rule as `zoomFactorOf` applies. Cheap enough to call on every open.
 */
export function syncMenuLayerZoom(layer: HTMLElement): void {
  // The layer's own zoom must not pollute the reading, so measure the parent.
  const parent = layer.parentElement ?? document.body;
  const factor = zoomFactorOf(parent);
  layer.style.zoom = factor === 1 ? '' : String(1 / factor);
  layer.style.setProperty(MENU_ZOOM_VAR, String(factor));
}
